import { useMemo, useState } from 'react';
import { BrainOrb, Button, Card, ChipStrip, GlassPill } from '../components/primitives';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import {
  useTeamPrinciples,
  type PrincipleRow,
  type PrincipleType,
  PRINCIPLE_TYPES,
} from '../lib/queries/useTeamPrinciples';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isPlaceholderSupabase } from '../lib/supabase';
import './page-shell.css';
import './Principles.css';

const TYPE_META: Record<PrincipleType, { label: string; description: string; glyph: string }> = {
  ATTACK: { label: 'Attack', description: 'In-possession ideas — how we want to play with the ball.', glyph: '↑' },
  DEFEND: { label: 'Defend', description: 'Out-of-possession ideas — how we want to win it back.', glyph: '↓' },
  TRANSITION: { label: 'Transition', description: 'Moments after winning or losing the ball.', glyph: '↔' },
  RESTART: { label: 'Restart', description: 'Set-pieces and dead-ball moments.', glyph: '◯' },
};

// Corner id → label. Mirrors packages/core seed without re-importing the
// whole types module — these four are stable.
const CORNERS: { id: string; label: string; short: 'T/T' | 'P' | 'Ψ' | 'S' }[] = [
  { id: 'corner-technical', label: 'Technical / Tactical', short: 'T/T' },
  { id: 'corner-physical', label: 'Physical', short: 'P' },
  { id: 'corner-psych', label: 'Psychological', short: 'Ψ' },
  { id: 'corner-social', label: 'Social', short: 'S' },
];

const PSYCH_SOCIAL_IDS = new Set(['corner-psych', 'corner-social']);

const PLACEHOLDERS: Record<PrincipleType, string> = {
  ATTACK: 'e.g. "Play forward when the line is breakable, otherwise circulate."',
  DEFEND: 'e.g. "Press on first touch from the centre-back; recover if broken."',
  TRANSITION: "e.g. \"First thought after winning it: furthest pass forward that's on.\"",
  RESTART: 'e.g. "Score from the second phase, not the first ball."',
};

export function Principles() {
  const { team, loading: teamLoading } = useActiveTeam();
  const { user } = useAuth();
  const { rows, state, errorMessage, refresh } = useTeamPrinciples(team?.id, teamLoading);

  const [activeType, setActiveType] = useState<PrincipleType>('ATTACK');
  const [showForm, setShowForm] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [draftCorners, setDraftCorners] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const principlesByType = useMemo(() => {
    const grouped: Record<PrincipleType, PrincipleRow[]> = {
      ATTACK: [],
      DEFEND: [],
      TRANSITION: [],
      RESTART: [],
    };
    for (const r of rows) grouped[r.type]?.push(r);
    return grouped;
  }, [rows]);

  const counts = useMemo(
    () => Object.fromEntries(PRINCIPLE_TYPES.map((t) => [t, principlesByType[t].length])) as Record<
      PrincipleType,
      number
    >,
    [principlesByType],
  );

  const draftHasPsychSocial = Array.from(draftCorners).some((id) => PSYCH_SOCIAL_IDS.has(id));

  const handleSave = async () => {
    if (!team || !user) return;
    if (draftText.trim().length < 5) {
      setSaveError('Principle text is too short — write at least one sentence.');
      return;
    }
    // Psych-social floor rule (§04.1): every principle must tag ≥1
    // Psychological or Social corner. Surface this as a save-time
    // validation rather than a hard form gate so the coach sees WHY
    // the save was blocked.
    if (!draftHasPsychSocial) {
      setSaveError(
        'Tag at least one Psychological or Social corner. Every principle must speak to the mental or social side too.',
      );
      return;
    }
    if (isPlaceholderSupabase()) {
      setSaveError('Connect a Supabase backend to save principles.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    const { error } = await supabase.from('principles').insert({
      team_id: team.id,
      type: activeType,
      text: draftText.trim(),
      corner_ids: Array.from(draftCorners),
    });
    setSaving(false);
    if (error) {
      setSaveError(error.message);
      return;
    }
    setDraftText('');
    setDraftCorners(new Set());
    setShowForm(false);
    refresh();
  };

  const handleDelete = async (id: string) => {
    if (isPlaceholderSupabase()) return;
    // Soft-delete to match the schema's deleted_at convention.
    const { error } = await supabase
      .from('principles')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);
    if (!error) refresh();
  };

  const toggleCorner = (id: string) =>
    setDraftCorners((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const list = principlesByType[activeType];

  return (
    <main className="main page principles-page">
      <header className="page-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div>
          <span className="page-eyebrow">Layer 2 · philosophy</span>
          <h1 className="page-title">Principles</h1>
          <p className="page-sub">
            How you want your team to play across the four moments. Brain reads these on every session draft.
          </p>
        </div>
      </header>

      <section className="principles-tabs" aria-label="Principle type">
        <ChipStrip
          chips={PRINCIPLE_TYPES.map((t) => ({
            label: `${TYPE_META[t].glyph} ${TYPE_META[t].label} · ${counts[t]}`,
            value: t,
            active: activeType === t,
          }))}
          onSelect={(v) => {
            setActiveType(v as PrincipleType);
            setShowForm(false);
          }}
        />
        <p className="principles-type-desc">{TYPE_META[activeType].description}</p>
      </section>

      {state === 'loading' && <p className="page-empty">Loading…</p>}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn&apos;t load principles.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}

      <section className="principles-list" aria-live="polite">
        {list.length === 0 && (state === 'ok' || state === 'empty') && (
          <Card variant="opaque">
            <p className="page-empty">
              No {TYPE_META[activeType].label.toLowerCase()} principles yet. Add one — keep it short and game-ready.
            </p>
          </Card>
        )}
        {list.map((p) => (
          <Card key={p.id} variant="opaque">
            <div className="principles-card">
              <p className="principles-card__text">{p.text}</p>
              <div className="principles-card__pills">
                {p.corner_ids.map((cid) => {
                  const meta = CORNERS.find((c) => c.id === cid);
                  return meta ? (
                    <GlassPill key={cid} mono>
                      {meta.short}
                    </GlassPill>
                  ) : null;
                })}
                <button
                  type="button"
                  className="principles-card__delete"
                  onClick={() => void handleDelete(p.id)}
                  aria-label="Delete principle"
                >
                  ✕
                </button>
              </div>
            </div>
          </Card>
        ))}
      </section>

      {showForm ? (
        <Card variant="opaque">
          <div className="principles-form">
            <span className="page-eyebrow">New {TYPE_META[activeType].label} principle</span>
            <textarea
              className="principles-form__text"
              placeholder={PLACEHOLDERS[activeType]}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows={3}
              maxLength={400}
              autoFocus
            />
            <div className="principles-form__corners">
              <span className="principles-form__corners-label">Corners (tag ≥1 Ψ or S):</span>
              {CORNERS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="principles-form__corner-chip"
                  data-active={draftCorners.has(c.id) || undefined}
                  data-required={PSYCH_SOCIAL_IDS.has(c.id) || undefined}
                  onClick={() => toggleCorner(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {saveError && <p className="principles-form__error">{saveError}</p>}
            <div className="principles-form__actions">
              <Button
                variant="primary"
                size="touchline"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save principle'}
              </Button>
              <Button
                variant="glass"
                size="touchline"
                onClick={() => {
                  setShowForm(false);
                  setSaveError(null);
                  setDraftText('');
                  setDraftCorners(new Set());
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <div className="principles-add-row">
          <Button variant="primary" size="touchline" onClick={() => setShowForm(true)}>
            + Add {TYPE_META[activeType].label.toLowerCase()} principle
          </Button>
        </div>
      )}
    </main>
  );
}
